import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { HOMEWORK } from "@/lib/desk/internLevels";
import { cn } from "@/lib/utils";

interface Row { id: string; user_id: string; assignment_id: string; image_path: string | null; explanation: string; status: string; ai_feedback: string | null; created_at: string }

export default function HomeworkReviewList() {
  const [filter, setFilter] = useState<"all" | "redo" | "passed">("all");
  const q = useQuery({
    queryKey: ["admin-homework", filter],
    queryFn: async () => {
      let qy = (supabase as any).from("academy_homework_submissions").select("*").order("created_at", { ascending: false }).limit(200);
      if (filter !== "all") qy = qy.eq("status", filter);
      const { data, error } = await qy;
      if (error) throw error;
      const rows = (data ?? []) as Row[];
      const ids = Array.from(new Set(rows.map((r) => r.user_id)));
      const { data: profs } = ids.length ? await supabase.from("profiles").select("user_id, display_name").in("user_id", ids) : { data: [] as any[] };
      const names: Record<string, string> = Object.fromEntries((profs ?? []).map((p: any) => [p.user_id, p.display_name || "Student"]));
      const paths = rows.map((r) => r.image_path).filter(Boolean) as string[];
      const { data: signed } = paths.length ? await supabase.storage.from("homework").createSignedUrls(paths, 3600) : { data: [] as any[] };
      const urls: Record<string, string> = Object.fromEntries((signed ?? []).map((s: any) => [s.path, s.signedUrl]));
      return rows.map((r) => ({ ...r, name: names[r.user_id] ?? "Student", url: r.image_path ? urls[r.image_path] : null }));
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {(["all", "redo", "passed"] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={cn("px-3 h-8 rounded-md border text-sm capitalize", filter === f ? "border-gold bg-gold/15" : "border-border")}>{f}</button>
        ))}
      </div>
      {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {q.data?.length === 0 && <p className="text-sm text-muted-foreground">No submissions yet.</p>}
      {q.data?.map((r) => <Item key={r.id} r={r} />)}
    </div>
  );
}

function Item({ r }: { r: Row & { name: string; url: string | null } }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const hw = HOMEWORK.find((h) => h.id === r.assignment_id);
  const act = async (status: "passed" | "redo") => {
    setBusy(true);
    const { error } = await supabase.functions.invoke("grade-homework", { body: { action: "review", id: r.id, status, note } });
    setBusy(false);
    if (error) {
      const msg = error instanceof FunctionsHttpError ? (await error.context.json().catch(() => ({})))?.error : error.message;
      return toast.error(msg || "Couldn't save.");
    }
    toast.success(status === "passed" ? "Marked passed" : "Sent back to redo");
    setNote("");
    qc.invalidateQueries({ queryKey: ["admin-homework"] });
  };
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">{r.name} · Level {hw?.level} · {hw?.title ?? r.assignment_id}</p>
          <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</p>
        </div>
        <span className={cn("text-xs font-semibold px-2 py-1 rounded-md", r.status === "passed" ? "bg-gold/20 text-gold-dark" : "bg-destructive/10 text-destructive")}>{r.status === "passed" ? "Passed" : "Redo"}</span>
      </div>
      <div className="mt-3 grid md:grid-cols-[220px_1fr] gap-3">
        {r.url ? <a href={r.url} target="_blank" rel="noreferrer"><img src={r.url} alt="Submitted chart" className="rounded-md border border-border w-full object-cover max-h-48" /></a> : <div className="text-xs text-muted-foreground">No chart</div>}
        <div className="space-y-2">
          <p className="text-sm whitespace-pre-wrap">{r.explanation}</p>
          {r.ai_feedback && <p className="text-xs text-muted-foreground border-l-2 border-gold/50 pl-2">{r.ai_feedback}</p>}
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to the member (required for Redo)" className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" />
          <div className="flex gap-2">
            <button disabled={busy} onClick={() => act("passed")} className="h-9 px-4 rounded-md bg-gold text-sm font-semibold disabled:opacity-50" style={{ color: "#1a1a1a" }}>Pass</button>
            <button disabled={busy} onClick={() => act("redo")} className="h-9 px-4 rounded-md border border-destructive text-destructive text-sm font-semibold disabled:opacity-50">Redo</button>
          </div>
        </div>
      </div>
    </div>
  );
}
